import React, { useEffect, useMemo, useState } from 'react';
import { X, CheckCircle2, AlertTriangle } from 'lucide-react';

interface ApprovalContextItem {
  label: string;
  value: React.ReactNode;
}

interface ApprovalWorkflowDrawerProps {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  contextItems?: ApprovalContextItem[];
  onClose: () => void;
  onApprove?: () => Promise<void> | void;
  onReject?: (reason: string) => Promise<void> | void;
  requireRejectionReason?: boolean;
  rejectionLabel?: string;
  rejectionPlaceholder?: string;
  minimumReasonLength?: number;
  children?: React.ReactNode;
}

const ApprovalWorkflowDrawer: React.FC<ApprovalWorkflowDrawerProps> = ({
  isOpen,
  title,
  subtitle,
  contextItems = [],
  onClose,
  onApprove,
  onReject,
  requireRejectionReason = true,
  rejectionLabel = 'Rejection Reason',
  rejectionPlaceholder = 'Explain why this item is being rejected.',
  minimumReasonLength = 10,
  children,
}) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [activeAction, setActiveAction] = useState<'approve' | 'reject' | null>(null);

  const canSubmitReject = useMemo(() => {
    if (!requireRejectionReason) return true;
    return rejectionReason.trim().length >= minimumReasonLength;
  }, [minimumReasonLength, rejectionReason, requireRejectionReason]);

  useEffect(() => {
    if (!isOpen) {
      setRejectionReason('');
      setReasonError('');
      setActiveAction(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleApprove = async () => {
    if (!onApprove) return;
    try {
      setActiveAction('approve');
      await onApprove();
      onClose();
    } catch {
      // Errors are surfaced by callers via existing notification system.
    } finally {
      setActiveAction(null);
    }
  };

  const handleReject = async () => {
    if (!onReject) return;
    const normalizedReason = rejectionReason.trim();

    if (requireRejectionReason && normalizedReason.length < minimumReasonLength) {
      setReasonError(`Reason must be at least ${minimumReasonLength} characters.`);
      return;
    }

    try {
      setActiveAction('reject');
      setReasonError('');
      await onReject(normalizedReason);
      onClose();
    } catch {
      // Errors are surfaced by callers via existing notification system.
    } finally {
      setActiveAction(null);
    }
  };

  return (
    <>
      <div className="approval-drawer-backdrop" onClick={onClose} />
      <aside className="approval-workflow-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="approval-workflow-drawer__header">
          <div>
            <p className="approval-workflow-drawer__eyebrow">Approval Workflow</p>
            <h3>{title}</h3>
            {subtitle && <p className="approval-workflow-drawer__subtitle">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="approval-workflow-drawer__close"
            aria-label="Close approval drawer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="approval-workflow-drawer__body">
          {contextItems.length > 0 && (
            <div className="approval-workflow-drawer__context">
              {contextItems.map((item) => (
                <div key={item.label} className="approval-workflow-drawer__context-row">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          )}

          {onReject && (
            <div className="approval-workflow-drawer__reason-block">
              <label htmlFor="approval-reason">{rejectionLabel}</label>
              <textarea
                id="approval-reason"
                value={rejectionReason}
                onChange={(event) => setRejectionReason(event.target.value)}
                placeholder={rejectionPlaceholder}
                rows={4}
              />
              {reasonError && <p className="approval-workflow-drawer__reason-error">{reasonError}</p>}
            </div>
          )}

          {children && <div className="approval-workflow-drawer__custom">{children}</div>}
        </div>

        <div className="approval-workflow-drawer__actions">
          {onApprove && (
            <button
              type="button"
              className="btn-primary approval-workflow-drawer__action"
              onClick={handleApprove}
              disabled={activeAction !== null}
            >
              <CheckCircle2 size={15} />
              {activeAction === 'approve' ? 'Approving...' : 'Approve'}
            </button>
          )}
          {onReject && (
            <button
              type="button"
              className="btn-danger approval-workflow-drawer__action"
              onClick={handleReject}
              disabled={activeAction !== null || !canSubmitReject}
            >
              <AlertTriangle size={15} />
              {activeAction === 'reject' ? 'Rejecting...' : 'Reject'}
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default ApprovalWorkflowDrawer;
